(() => {
  if (customElements.get('wuyin-home-module')) return;
  class WuyinHomepage extends HTMLElement {
    connectedCallback() { this.init(); }
    disconnectedCallback() { this.destroy(); }
    init() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.slides = Array.from(this.querySelectorAll('[data-edition]'));
      this.index = 0;
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.dataset.autoplay !== 'true' || this.reduced.matches || !this.querySelector('[data-edition-pause]') || Boolean(window.Shopify?.designMode);
      this.visible = false;
      this.changed = Date.now();
      this.querySelector('[data-edition-controls]')?.removeAttribute('hidden');
      this.querySelector('[data-filters]')?.removeAttribute('hidden');
      this.addEventListener('click', event => this.onClick(event), { signal });
      this.addEventListener('focusin', event => {
        if (event.target.closest('[data-editions]') && event.target.matches(':focus-visible')) { this.paused = true; this.updatePause(); }
      }, { signal });
      this.reduced.addEventListener('change', event => { if (event.matches) { this.paused = true; this.updatePause(); } }, { signal });
      const carousel = this.querySelector('[data-editions]');
      if (carousel) {
        this.observer = new IntersectionObserver(entries => {
          const visible = entries[0].isIntersecting;
          if (visible !== this.visible) this.changed = Date.now();
          this.visible = visible;
        });
        this.observer.observe(carousel);
      }
      this.showEdition(0);
      this.countdownUpdated = Date.now();
      if (this.slides.length) this.timer = setInterval(() => {
        const now = Date.now();
        if (document.hidden || !this.visible) { this.changed = now; return; }
        if (now - this.countdownUpdated >= 1000) { this.updateCountdown(); this.countdownUpdated = now; }
        if (this.slides.length > 1 && !this.paused && now - this.changed >= 3000) this.showEdition(this.index + 1);
      }, 250);
    }
    destroy() {
      this.controller?.abort();
      this.controller = null;
      clearInterval(this.timer);
      this.observer?.disconnect();
    }
    updatePause() {
      const button = this.querySelector('[data-edition-pause]');
      if (button) button.textContent = this.paused ? this.dataset.playLabel : this.dataset.pauseLabel;
    }
    showEdition(index, announce = false) {
      if (!this.slides.length) return;
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => { slide.hidden = i !== this.index; });
      this.querySelectorAll('[data-edition-index]').forEach(button => button.setAttribute('aria-current', String(Number(button.dataset.editionIndex) === this.index)));
      this.changed = Date.now();
      this.updatePause();
      this.updateCountdown();
      if (announce) this.querySelector('[data-edition-status]').textContent = this.slides[this.index].getAttribute('aria-label');
    }
    updateCountdown() {
      const slide = this.slides[this.index];
      if (!slide) return;
      const label = slide.querySelector('[data-launch-state]');
      const timer = slide.querySelector('[data-countdown]');
      const date = slide.querySelector('[data-launch-date]');
      const source = slide.dataset.launch;
      const showSchedule = slide.dataset.countdownEnabled === 'true';
      label.hidden = !showSchedule;
      date.hidden = !showSchedule;
      timer.hidden = true;
      if (!showSchedule) return;
      const zoned = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?(?:Z|[+-]\d{2}:\d{2})$/.test(source);
      const timestamp = zoned ? Date.parse(source) : NaN;
      const valid = Number.isFinite(timestamp);
      timer.hidden = true;
      if (!valid) {
        label.textContent = this.dataset.unannouncedLabel;
        date.hidden = true;
        timer.hidden = false;
        timer.setAttribute('role', 'group');
        timer.querySelectorAll('[data-unit]').forEach(unit => { unit.textContent = '—'; });
        return;
      }
      timer.setAttribute('role', 'timer');
      date.textContent = new Intl.DateTimeFormat(document.documentElement.lang || 'en', { timeZone: this.dataset.timeZone, timeZoneName: 'short', year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).format(timestamp);
      const seconds = Math.max(0, Math.ceil((timestamp - Date.now()) / 1000));
      if (seconds === 0) { label.textContent = this.dataset.demo === 'true' ? this.dataset.demoStartedLabel : (slide.dataset.productAvailable === 'true' ? this.dataset.availableLabel : this.dataset.startedLabel); return; }
      if (slide.dataset.countdownEnabled !== 'true') { label.textContent = this.dataset.upcomingLabel; return; }
      label.textContent = this.dataset.countdownLabel;
      timer.hidden = false;
      const values = [Math.floor(seconds / 86400), Math.floor(seconds / 3600) % 24, Math.floor(seconds / 60) % 60, seconds % 60];
      timer.querySelectorAll('[data-unit]').forEach(unit => { unit.textContent = String(values[Number(unit.dataset.unit)]).padStart(2, '0'); });
    }
    showWorld(id) {
      const target = this.querySelector(`[data-world-panel="${CSS.escape(id)}"]`);
      if (!target) return;
      this.querySelectorAll('[data-world-panel]').forEach(panel => { panel.hidden = panel !== target; });
      this.querySelectorAll('[data-world-target]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.worldTarget === id)));
    }
    filterWorks(group) {
      const panel = this.querySelector(`[data-work-panel="${CSS.escape(group)}"]`);
      if (!panel) return;
      this.querySelectorAll('[data-work-panel]').forEach(item => { item.hidden = item !== panel; });
      this.querySelectorAll('[data-filter]').forEach(control => control.setAttribute(control.tagName === 'A' ? 'aria-current' : 'aria-pressed', String(control.dataset.filter === group)));
      panel.querySelector('[data-work-grid]')?.scrollTo({ left: 0, behavior: 'auto' });
      const active = this.querySelector(`[data-filter="${CSS.escape(group)}"]`);
      const status = this.querySelector('[data-filter-status]');
      if (status && active) status.textContent = `${active.textContent}: ${panel.querySelectorAll('.wyhp-product').length}`;
    }
    onClick(event) {
      const filter = event.target.closest('[data-filter]');
      if (filter && this.contains(filter)) {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.filterWorks(filter.dataset.filter);
        return;
      }
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-edition-index') || button.hasAttribute('data-edition-step')) {
        this.paused = true;
        this.showEdition(button.hasAttribute('data-edition-index') ? Number(button.dataset.editionIndex) : this.index + Number(button.dataset.editionStep), true);
      }
      if (button.hasAttribute('data-edition-pause')) { this.paused = !this.paused; this.changed = Date.now(); this.updatePause(); }
      if (button.dataset.worldTarget) this.showWorld(button.dataset.worldTarget);
    }
    selectBlock(id) {
      const slideIndex = this.slides.findIndex(slide => slide.dataset.blockId === id);
      if (slideIndex >= 0) { this.paused = true; this.showEdition(slideIndex); }
      this.showWorld(id);

    }
  }
  customElements.define('wuyin-home-module', WuyinHomepage);
  document.addEventListener('shopify:section:load', event => { event.target.querySelectorAll('wuyin-home-module').forEach(element => element.init()); });
  document.addEventListener('shopify:section:unload', event => { event.target.querySelectorAll('wuyin-home-module').forEach(element => element.destroy()); });
  document.addEventListener('shopify:block:select', event => {
    const element = event.target.closest('wuyin-home-module');
    if (element && event.detail?.blockId) element.selectBlock(event.detail.blockId);
  });
})();
